import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { projectsData } from '../i18n/translations';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import SEO from '../components/SEO';

const Home = () => {
  const { language, t } = useLanguage();
  
  const inProgressProjects = projectsData.filter(p => p.status === 'inProgress');

  const seoData = {
    es: {
      title: 'PASTORIA SERRALTA | Promotor Inmobiliario en Baleares - Viviendas de Calidad',
      description: 'PASTORIA SERRALTA, promotor inmobiliario de referencia en las Islas Baleares. Viviendas de calidad para la clase media residente en Palma de Mallorca. Promociones en curso y terminadas.'
    },
    en: {
      title: 'PASTORIA SERRALTA | Real Estate Developer in Balearic Islands - Quality Homes',
      description: 'PASTORIA SERRALTA, leading real estate developer in the Balearic Islands. Quality homes for local middle-class residents in Palma de Mallorca. Ongoing and completed projects.'
    }
  };

  return (
    <div className="font-montserrat-bold min-h-screen" data-testid="home-page">
      <SEO 
        title={seoData[language].title}
        description={seoData[language].description}
        url={language === 'es' ? '/' : '/en'}
        image="https://www.pastoria-serralta.com/og-image.jpg"
      />
      {/* Hero Section */}
      <section className="font-montserrat-bold relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="font-montserrat-bold absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/image28.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="font-montserrat-bold absolute inset-0 bg-gradient-to-r from-[#356C8A]/90 to-[#2B7FD9]/80" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
            data-testid="hero-title"
          >
            {t.home.hero.title}
          </h1>
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-200 mb-8 max-w-2xl mx-auto">
            {t.home.hero.subtitle}
          </p>
          <Link
            to={language === 'es' ? '/promociones' : '/en/projects'}
            className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-[#356C8A] rounded-full font-semibold hover:bg-gray-100 transition-all transform hover:scale-105"
            data-testid="hero-cta-button"
          >
            <span>{t.home.hero.cta}</span>
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* Projects in Progress */}
      <section className="font-montserrat-bold py-20 bg-[#E8F2F7]">
        <div className="font-montserrat-bold max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="font-montserrat-bold text-center mb-12">
            <h2 className="font-montserrat-bold text-4xl sm:text-5xl font-bold text-[#356C8A] mb-4" data-testid="projects-section-title">
              {t.home.projects.title}
            </h2>
          </div>

          <div className="font-montserrat-bold grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {inProgressProjects.map((project) => (
              <Link
                key={project.id}
                to={`${language === 'es' ? '/promociones' : '/en/projects'}/${project.slug}`}
                className="font-montserrat-bold group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-all"
                data-testid={`project-card-${project.slug}`}
              >
                <div className="font-montserrat-bold relative h-56 overflow-hidden">
                  <img
                    src={`/${project.images[0]}`}
                    alt={project.name}
                    className="font-montserrat-bold w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="font-montserrat-bold absolute top-4 right-4 bg-[#356C8A] text-white px-4 py-2 rounded-full font-bold text-sm">
                    {project.soldPercentage}% {t.projects.status.sold}
                  </div>
                </div>
                <div className="font-montserrat-bold p-6">
                  <h3 className="font-montserrat-bold text-xl font-bold text-[#356C8A] mb-2">
                    {project.name}
                  </h3>
                  <p className="font-montserrat-bold text-gray-600 text-sm mb-4">{project.location}</p>
                  <p className="font-montserrat-bold text-gray-700 line-clamp-2">
                    {project.description[language]}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          <div className="font-montserrat-bold text-center mt-12">
            <Link
              to={language === 'es' ? '/promociones' : '/en/projects'}
              className="font-montserrat-bold inline-flex items-center space-x-2 px-6 py-3 border-2 border-[#356C8A] text-[#356C8A] rounded-full font-semibold hover:bg-[#356C8A] hover:text-white transition-colors"
              data-testid="view-all-projects-button"
            >
              <span>{t.home.projects.viewAll}</span>
              <ArrowRight className="font-montserrat-bold h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="font-montserrat-bold py-20 bg-white">
        <div className="font-montserrat-bold max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="font-montserrat-bold bg-[#E8F2F7] rounded-2xl p-8 sm:p-12">
            <div className="font-montserrat-bold flex items-start space-x-4 mb-6">
              <CheckCircle2 className="font-montserrat-bold h-8 w-8 text-[#356C8A] flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-montserrat-bold text-3xl font-bold text-[#356C8A] mb-4" data-testid="mission-title">
                  {t.home.mission.title}
                </h2>
                <p className="font-montserrat-bold text-lg text-gray-700 leading-relaxed">
                  {t.home.mission.text}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;